import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AuthGuard from './AuthGuard';
import AppLoader from './AppLoader';
import env from '../config/env';

interface PrivateRouteProps {
  children: React.ReactNode;
}

/**
 * PrivateRoute - Two-step route protection
 *
 * 1. Authentication Check: User must be logged in (has valid token)
 * 2. Authorization Check (AuthGuard): User must have route access permissions
 *
 * This ensures that even if a user is authenticated, they can only access
 * routes that are explicitly assigned to them via their role/profile.
 *
 * Note: AuthGuard can be disabled via VITE_ENABLE_AUTH_GUARD=true in .env
 */
const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <AppLoader message="Initializing application..." />;
  }

  // Step 1: Check authentication (is user logged in?)
  if (!isAuthenticated) {
    // Redirect to login with return url (include search params if any)
    const returnUrl = location.pathname + location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(returnUrl)}`} replace />;
  }

  // Step 2: Check authorization (does user have access to this route?)
  // Skip AuthGuard if disabled via environment variable
  if (!env.ENABLE_AUTH_GUARD) {
    return <>{children}</>;
  }

  return (
    <AuthGuard>
      {children}
    </AuthGuard>
  );
};

export default PrivateRoute;